import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { useSelectedUser } from '../users/hooks/useSelectedUser';

import {
    addAlias,
    CATEGORY_COLORS,
    correctFact,
    getMemory,
    isPartialDate,
    MemoryFact,
    MemoryOverview,
    MemoryPerson,
    previewRecall,
    RecallPreview,
    retireFact,
    whenLabel,
} from '../memory/memoryApi';
import { RootState } from '../redux/store';
import { errorMessage } from '../replays/replaysApi';
import { GlowAlert } from '../shared/components/GlowAlert';
import { LoadingButton } from '../shared/components/LoadingButton';
import { Section } from '../shared/components/Section';

type Actions = {
    /** What is being changed right now (`retire:<id>`, `alias:<id>`, ...); every action waits for it. */
    pending: string | null;
    onEdit: (fact: MemoryFact) => void;
    onRetire: (fact: MemoryFact) => void;
};

const Stat: React.FC<{ label: string; value: number }> = ({ label, value }) => (
    <Box sx={{ minWidth: 110 }}>
        <Typography variant="h5">{value}</Typography>
        <Typography variant="caption" color="text.secondary">
            {label}
        </Typography>
    </Box>
);

/** The facts of one subject. Open ones can be corrected or retired; history is read-only. */
const FactsTable: React.FC<{ facts: MemoryFact[]; actions?: Actions; showPerson?: boolean }> = ({
    facts,
    actions,
    showPerson,
}) => (
    <Table size="small">
        <TableHead>
            <TableRow>
                {showPerson && <TableCell>About</TableCell>}
                <TableCell>Category</TableCell>
                <TableCell>Key</TableCell>
                <TableCell>Value</TableCell>
                <TableCell>Confidence</TableCell>
                <TableCell>When</TableCell>
                {actions && <TableCell />}
            </TableRow>
        </TableHead>
        <TableBody>
            {facts.map((fact) => (
                <TableRow key={fact.id} hover>
                    {showPerson && <TableCell>{fact.person ?? 'the user'}</TableCell>}
                    <TableCell>
                        <Chip size="small" label={fact.category} color={CATEGORY_COLORS[fact.category] ?? 'default'} />
                    </TableCell>
                    <TableCell>{fact.key}</TableCell>
                    <TableCell>
                        <Tooltip
                            arrow
                            title={fact.source_excerpt ? `From: “${fact.source_excerpt}”` : 'No source recorded'}
                        >
                            <span>{fact.value}</span>
                        </Tooltip>
                    </TableCell>
                    <TableCell>{fact.confidence.toFixed(2)}</TableCell>
                    <TableCell>{whenLabel(fact)}</TableCell>
                    {actions && (
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                            <Button size="small" disabled={actions.pending !== null} onClick={() => actions.onEdit(fact)}>
                                Edit
                            </Button>
                            {!fact.valid_to && (
                                <LoadingButton
                                    size="small"
                                    color="warning"
                                    loading={actions.pending === `retire:${fact.id}`}
                                    disabled={actions.pending !== null}
                                    onClick={() => actions.onRetire(fact)}
                                >
                                    No longer true
                                </LoadingButton>
                            )}
                        </TableCell>
                    )}
                </TableRow>
            ))}
        </TableBody>
    </Table>
);

const PersonCard: React.FC<{
    person: MemoryPerson;
    actions: Actions;
    onAlias: (person: MemoryPerson, alias: string) => Promise<void>;
}> = ({ person, actions, onAlias }) => {
    const [alias, setAlias] = useState('');
    return (
        <Accordion disableGutters variant="outlined">
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography variant="subtitle1">{person.name}</Typography>
                    {person.relationship && <Chip size="small" variant="outlined" label={person.relationship} />}
                    <Typography variant="caption" color="text.secondary">
                        {person.open.length} remembered · {person.history.length} earlier
                    </Typography>
                </Box>
            </AccordionSummary>
            <AccordionDetails>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                        Also called:
                    </Typography>
                    {person.aliases.map((a) => (
                        <Chip key={a} size="small" label={a} />
                    ))}
                    <TextField
                        size="small"
                        placeholder="Add another name"
                        value={alias}
                        onChange={(e) => setAlias(e.target.value)}
                        inputProps={{ 'aria-label': `Add another name for ${person.name}` }}
                    />
                    <LoadingButton
                        size="small"
                        loading={actions.pending === `alias:${person.id}`}
                        disabled={!alias.trim() || actions.pending !== null}
                        onClick={async () => {
                            await onAlias(person, alias);
                            setAlias('');
                        }}
                    >
                        Add name
                    </LoadingButton>
                </Box>
                {person.open.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        Nothing remembered about them yet.
                    </Typography>
                ) : (
                    <FactsTable facts={person.open} actions={actions} />
                )}
                {person.history.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="overline" color="text.secondary">
                            Earlier, no longer true
                        </Typography>
                        <FactsTable facts={person.history} />
                    </Box>
                )}
            </AccordionDetails>
        </Accordion>
    );
};

const RecallTab: React.FC<{ userId: string }> = ({ userId }) => {
    const [message, setMessage] = useState('');
    const [preview, setPreview] = useState<RecallPreview | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [running, setRunning] = useState(false);

    const run = async () => {
        if (running) return;
        setError(null);
        setRunning(true);
        try {
            setPreview(await previewRecall(userId, message));
        } catch (err) {
            setError(errorMessage(err, 'Could not preview the recall'));
        } finally {
            setRunning(false);
        }
    };

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Type a message the user might send. This shows the memory block a reply to it would be given, chosen from
                what is remembered now. People the message names bring everything known about them.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                    fullWidth
                    size="small"
                    label="Message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && run()}
                />
                <LoadingButton variant="contained" onClick={run} loading={running}>
                    Preview
                </LoadingButton>
            </Box>
            {error && <GlowAlert sx={{ mb: 2 }}>{error}</GlowAlert>}
            {preview && (
                <>
                    {!preview.enabled && (
                        <GlowAlert severity="info" sx={{ mb: 2 }}>
                            MEMORY_FACTS_ENABLED is off on the backend, so replies are not given this block.
                        </GlowAlert>
                    )}
                    <Typography variant="caption" color="text.secondary">
                        {preview.chars} of {preview.budget} characters · {preview.candidates} facts to choose from
                        {preview.named.length > 0 && ` · names in the message: ${preview.named.join(', ')}`}
                    </Typography>
                    <Box
                        component="pre"
                        sx={{ mt: 1, p: 2, border: 1, borderColor: 'divider', borderRadius: 1, whiteSpace: 'pre-wrap', fontSize: 13 }}
                    >
                        {preview.block || 'Nothing would be added to the prompt.'}
                    </Box>
                </>
            )}
        </Box>
    );
};

const MemoryPage: React.FC = () => {
    const users = useSelector((state: RootState) => state.users.users);
    const [selectedUserId, setUserId] = useSelectedUser();
    // A remembered user who no longer exists is ignored.
    const userId = users.some((u) => u.id === selectedUserId) ? selectedUserId : '';
    const [overview, setOverview] = useState<MemoryOverview | null>(null);
    const [loading, setLoading] = useState(false);
    const [pending, setPending] = useState<string | null>(null);
    const [tab, setTab] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [editing, setEditing] = useState<MemoryFact | null>(null);
    const [draft, setDraft] = useState('');
    const [draftFrom, setDraftFrom] = useState('');
    const [draftUntil, setDraftUntil] = useState('');

    const load = useCallback(async () => {
        if (!userId) {
            setOverview(null);
            return;
        }
        setLoading(true);
        try {
            setError(null);
            setOverview(await getMemory(userId));
        } catch (err) {
            setError(errorMessage(err, 'Could not load memory'));
        } finally {
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        if (!userId && users.length > 0) {
            setUserId(users[0].id);
        }
    }, [users, userId, setUserId]);

    useEffect(() => {
        load();
    }, [load]);

    // Changes are shown after the server has accepted them, never before.
    const change = async (key: string, action: () => Promise<void>, done: string) => {
        if (pending !== null) return;
        setPending(key);
        try {
            await action();
            setNotice(done);
            setError(null);
            await load();
        } catch (err) {
            setNotice(null);
            setError(errorMessage(err, 'The change could not be saved'));
        } finally {
            setPending(null);
        }
    };

    const actions: Actions = {
        pending,
        onEdit: (fact) => {
            setEditing(fact);
            setDraft(fact.value);
            setDraftFrom(fact.valid_from ?? '');
            setDraftUntil(fact.valid_until ?? '');
        },
        onRetire: (fact) => change(`retire:${fact.id}`, () => retireFact(fact.id), `“${fact.key}” is marked as no longer true.`),
    };

    const onAlias = (person: MemoryPerson, alias: string) =>
        change(`alias:${person.id}`, () => addAlias(person.id, alias.trim()), `${person.name} is also called “${alias.trim()}”.`);

    return (
        <Section
            title="Memory"
            onRefresh={load}
            refreshing={loading}
            subtitle="What VictorIA remembers about the user and the people in their life. A fact that changes is closed and kept as history."
        >
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap', mb: 3 }}>
                <TextField
                    select
                    size="small"
                    label="User"
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                    sx={{ minWidth: 220 }}
                >
                    {users.map((user) => (
                        <MenuItem key={user.id} value={user.id}>
                            {user.display_name}
                        </MenuItem>
                    ))}
                </TextField>
                {overview && (
                    <>
                        <Stat label="remembered" value={overview.totals.open} />
                        <Stat label="earlier" value={overview.totals.closed} />
                        <Stat label="people" value={overview.totals.people} />
                    </>
                )}
            </Box>

            {error && <GlowAlert sx={{ mb: 2 }}>{error}</GlowAlert>}
            {notice && (
                <GlowAlert severity="success" sx={{ mb: 2 }}>
                    {notice}
                </GlowAlert>
            )}

            <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 2 }}>
                <Tab label="People" />
                <Tab label="About the user" />
                <Tab label="Newest" />
                <Tab label="Recall preview" />
            </Tabs>

            {tab === 3 && (userId ? <RecallTab userId={userId} /> : <GlowAlert severity="info">Pick a user.</GlowAlert>)}

            {tab !== 3 && !overview && !error && (
                <GlowAlert severity="info">{loading ? 'Loading memory...' : 'Nothing loaded yet.'}</GlowAlert>
            )}

            {tab === 0 && overview && (
                <Box sx={{ display: 'grid', gap: 1 }}>
                    {overview.people.length === 0 && (
                        <GlowAlert severity="info">
                            No people yet. They appear when the user mentions someone, or names them in a photo.
                        </GlowAlert>
                    )}
                    {overview.people.map((person) => (
                        <PersonCard key={person.id} person={person} actions={actions} onAlias={onAlias} />
                    ))}
                </Box>
            )}

            {tab === 1 && overview && (
                <>
                    {overview.self.open.length === 0 ? (
                        <GlowAlert severity="info">Nothing remembered about the user themself yet.</GlowAlert>
                    ) : (
                        <FactsTable facts={overview.self.open} actions={actions} />
                    )}
                    {overview.self.history.length > 0 && (
                        <Box sx={{ mt: 3 }}>
                            <Typography variant="overline" color="text.secondary">
                                Earlier, no longer true
                            </Typography>
                            <FactsTable facts={overview.self.history} />
                        </Box>
                    )}
                </>
            )}

            {tab === 2 && overview && (
                <>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        The latest facts written, newest first. After sending VictorIA a message, refresh to see what it
                        produced; hover a value to see the words it was taken from.
                    </Typography>
                    {overview.recent.length === 0 ? (
                        <GlowAlert severity="info">Nothing has been extracted yet.</GlowAlert>
                    ) : (
                        <FactsTable facts={overview.recent} showPerson />
                    )}
                </>
            )}

            <Dialog open={editing !== null} onClose={() => pending === null && setEditing(null)} fullWidth maxWidth="sm">
                <DialogTitle>Fix “{editing?.key}”</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        margin="dense"
                        label="Value"
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                    />
                    <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                        <TextField
                            size="small"
                            label="True from"
                            placeholder="2011"
                            value={draftFrom}
                            onChange={(e) => setDraftFrom(e.target.value)}
                            error={draftFrom.trim() !== '' && !isPartialDate(draftFrom)}
                            helperText="A year, month or day: 2011, 2012-03, 2012-03-04"
                        />
                        <TextField
                            size="small"
                            label="True until"
                            placeholder="2012"
                            value={draftUntil}
                            onChange={(e) => setDraftUntil(e.target.value)}
                            error={draftUntil.trim() !== '' && !isPartialDate(draftUntil)}
                            helperText="Empty: still true, or not known"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditing(null)} disabled={pending !== null}>
                        Cancel
                    </Button>
                    <LoadingButton
                        variant="contained"
                        loading={pending === 'save'}
                        disabled={
                            !draft.trim() ||
                            (draftFrom.trim() !== '' && !isPartialDate(draftFrom)) ||
                            (draftUntil.trim() !== '' && !isPartialDate(draftUntil)) ||
                            (draft.trim() === editing?.value &&
                                draftFrom.trim() === (editing?.valid_from ?? '') &&
                                draftUntil.trim() === (editing?.valid_until ?? ''))
                        }
                        onClick={async () => {
                            const fact = editing;
                            if (fact) {
                                await change(
                                    'save',
                                    () =>
                                        correctFact(fact.id, {
                                            value: draft.trim(),
                                            valid_from: draftFrom.trim(),
                                            valid_until: draftUntil.trim(),
                                        }),
                                    `“${fact.key}” is updated.`
                                );
                                setEditing(null);
                            }
                        }}
                    >
                        Save
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Section>
    );
};

export default MemoryPage;
